import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { Notification } from '@progress/kendo-react-notification';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authService } from '../../..';
import { httpGetAsync, httpPostAsync, httpPatchAsync, httpDeleteAsync } from '../../../utils/HttpClient';
import { SecureCloseWebhookSubscription } from '../../../Interfaces/ESign/secureCloseTypes';

const AVAILABLE_EVENT_TYPES = [
  'signing_created',
  'signing_started',
  'signing_completed',
];

interface WebhookConfigPanelProps {
  onBack: () => void;
}

export default function WebhookConfigPanel({ onBack }: WebhookConfigPanelProps) {
  const [webhooks, setWebhooks] = useState<SecureCloseWebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Edit/Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<SecureCloseWebhookSubscription | null>(null);
  const [dialogUrl, setDialogUrl] = useState('');
  const [dialogEventTypes, setDialogEventTypes] = useState<string[]>([]);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await authService.getToken();
      const response = await httpGetAsync('api/secureClose/webhooks', token!);
      if (response?.ok) {
        const data: SecureCloseWebhookSubscription[] = await response.json();
        setWebhooks(data);
      } else {
        const errText = await response?.text();
        setError(errText || 'Failed to load webhooks');
      }
    } catch {
      setError('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingWebhook(null);
    setDialogUrl('');
    setDialogEventTypes([...AVAILABLE_EVENT_TYPES]);
    setDialogOpen(true);
  };

  const openEditDialog = (webhook: SecureCloseWebhookSubscription) => {
    setEditingWebhook(webhook);
    setDialogUrl(webhook.url);
    setDialogEventTypes([...webhook.eventTypes]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = await authService.getToken();

      if (editingWebhook) {
        // Update
        const response = await httpPatchAsync(
          `api/secureClose/webhooks/${encodeURIComponent(editingWebhook.id)}`,
          { url: dialogUrl, eventTypes: dialogEventTypes },
          token!
        );
        if (!response?.ok) {
          const errText = await response?.text();
          setError(errText || 'Failed to update webhook');
          setSaving(false);
          return;
        }
      } else {
        // Create
        const response = await httpPostAsync(
          'api/secureClose/webhooks',
          { url: dialogUrl, eventTypes: dialogEventTypes },
          token!
        );
        if (!response?.ok) {
          const errText = await response?.text();
          setError(errText || 'Failed to create webhook');
          setSaving(false);
          return;
        }
      }

      setDialogOpen(false);
      await loadWebhooks();
    } catch {
      setError('Failed to save webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    setError(null);
    try {
      const token = await authService.getToken();
      const response = await httpDeleteAsync(
        `api/secureClose/webhooks/${encodeURIComponent(id)}`,
        token!
      );
      if (!response?.ok) {
        const errText = await response?.text();
        setError(errText || 'Failed to delete webhook');
      } else {
        await loadWebhooks();
      }
    } catch {
      setError('Failed to delete webhook');
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const canSaveDialog = dialogUrl.trim().length > 0 && dialogEventTypes.length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Tooltip title="Back to signing">
          <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Webhook Subscriptions
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Webhook
        </Button>
      </Box>

      {error && (
        <Notification type={{ style: 'error', icon: true }} closable onClose={() => setError(null)}>
          {error}
        </Notification>
      )}

      {webhooks.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No webhook subscriptions configured. Click "Add Webhook" to create one.
        </Typography>
      )}

      {webhooks.map((webhook) => (
        <Paper key={webhook.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap title={webhook.url}>
                {webhook.url}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                ID: {webhook.id}
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                {webhook.eventTypes.map((et) => (
                  <Chip key={et} label={et} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
            <Box display="flex" gap={0.5} ml={1}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEditDialog(webhook)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(webhook.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      ))}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Webhook URL"
            placeholder="https://your-server.com/webhook"
            value={dialogUrl}
            onChange={(e) => setDialogUrl(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Event Types</InputLabel>
            <Select
              multiple
              value={dialogEventTypes}
              label="Event Types"
              onChange={(e) => setDialogEventTypes(e.target.value as string[])}
              renderValue={(selected) => (
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {(selected as string[]).map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
            >
              {AVAILABLE_EVENT_TYPES.map((et) => (
                <MenuItem key={et} value={et}>{et}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSaveDialog || saving}
          >
            {saving ? <CircularProgress size={20} /> : editingWebhook ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Webhook</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this webhook subscription?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
